import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'

import { api, setAccessToken, setUnauthorizedHandler } from '../lib/api'
import type { Me } from '../lib/types'

interface AuthValue {
  me: Me | null
  loading: boolean
  can: (permission: string) => boolean
  login: (login: string, password: string) => Promise<void>
  register: (payload: {
    email: string; username?: string; password: string
    full_name: string; org_name: string
  }) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setUnauthorizedHandler(() => { setAccessToken(null); setMe(null) })
    // A page reload loses the in-memory access token but the refresh cookie
    // survives, so try to restore the session before showing the login form.
    ;(async () => {
      if (await api.tryRefresh()) {
        try { setMe(await api.me()) } catch { setMe(null) }
      }
      setLoading(false)
    })()
  }, [])

  const login = useCallback(async (handle: string, password: string) => {
    const { access_token } = await api.login(handle, password)
    setAccessToken(access_token)
    setMe(await api.me())
  }, [])

  const register = useCallback(async (payload: {
    email: string; username?: string; password: string
    full_name: string; org_name: string
  }) => {
    const { access_token } = await api.register(payload)
    setAccessToken(access_token)
    setMe(await api.me())
  }, [])

  const logout = useCallback(async () => {
    try { await api.logout() } finally {
      setAccessToken(null)
      setMe(null)
    }
  }, [])

  const can = useCallback(
    (permission: string) => me?.permissions.includes(permission) ?? false,
    [me],
  )

  const value = useMemo(
    () => ({ me, loading, can, login, register, logout }),
    [me, loading, can, login, register, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
